<script setup lang="ts">
import { ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { resolveRequestedTenantDomain } from '../../utils/tenantBootstrap'
import { ApiClient } from '@xjlv2/api-client'
import { RrAlert, RrButton, RrCard, RrForm, RrFormItem, RrInput } from '@xjlv2/ui'
import { User, Lock, RefreshCw, QrCode } from '@lucide/vue'

const props = defineProps<{
  loading: boolean
  error: string
}>()

const emit = defineEmits<{
  submit: [username: string, password: string, tenantDomain: string]
  loginFromResponse: [response: any]
}>()

// 租户域名通过 URL 或子域名隐式推导解析，表单界面不再需要用户手动输入
const tenantDomain = resolveRequestedTenantDomain(window.location.search, window.location.hostname)

const form = ref({
  username: '',
  password: ''
})

const usernameInputRef = ref<InstanceType<typeof RrInput> | null>(null)
const passwordInputRef = ref<InstanceType<typeof RrInput> | null>(null)
const hasShake = ref(false)
const activeError = ref(props.error)

// 监听父组件传来的错误，触发震动与密码框自动聚焦/全选
watch(() => props.error, (newVal) => {
  activeError.value = newVal
  if (newVal) {
    hasShake.value = true
    setTimeout(() => {
      hasShake.value = false
    }, 400)
    // 聚焦到密码框并全选已有内容，方便用户立刻重输
    passwordInputRef.value?.focus()
    const rawInput = passwordInputRef.value?.input as HTMLInputElement | undefined
    if (rawInput && typeof rawInput.select === 'function') {
      rawInput.select()
    }
  }
})

// 当用户修改账号或密码时，自动消除错误提示与红框
watch([() => form.value.username, () => form.value.password], () => {
  if (activeError.value) {
    activeError.value = ''
  }
})

const loginMode = ref<'password' | 'wechat'>('password')
const loginConfig = ref<{
  passwordLoginEnabled: boolean
  wechatScanEnabled: boolean
  wechatAppId: string | null
  loginPageTitle: string | null
  loginPageLogoUrl: string | null
} | null>(null)

const ticketId = ref<string | null>(null)
const qrImageBase64 = ref<string | null>(null)
const pollTimer = ref<number | null>(null)
const ticketExpired = ref(false)

onMounted(() => {
  loadLoginConfig()
  nextTick(() => {
    if (form.value.username) {
      passwordInputRef.value?.focus()
    } else {
      usernameInputRef.value?.focus()
    }
  })
})

onBeforeUnmount(() => {
  stopPolling()
})

async function loadLoginConfig() {
  const domain = tenantDomain.trim()
  if (!domain) {
    loginConfig.value = null
    return
  }
  try {
    const client = new ApiClient({ gateway: '/api' })
    const config = await client.get<typeof loginConfig.value>('/auth/login-config?tenantDomain=' + encodeURIComponent(domain))
    loginConfig.value = config
    if (config?.wechatScanEnabled && !config?.passwordLoginEnabled) {
      switchToWechat()
    }
  } catch {
    loginConfig.value = null
  }
}

async function switchToWechat() {
  loginMode.value = 'wechat'
  ticketExpired.value = false
  qrImageBase64.value = null
  ticketId.value = null
  stopPolling()
  const domain = tenantDomain.trim()
  try {
    const client = new ApiClient({ gateway: '/api' })
    const result = await client.post<{ ticketId: string; qrImageBase64: string; expiresAt: string }>(
      '/auth/wechat/ticket/create', { tenantDomain: domain })
    ticketId.value = result.ticketId
    qrImageBase64.value = result.qrImageBase64
    startPolling()
  } catch {
    ticketExpired.value = true
  }
}

function startPolling() {
  stopPolling()
  pollTimer.value = window.setInterval(async () => {
    if (!ticketId.value) return
    try {
      const client = new ApiClient({ gateway: '/api' })
      const result = await client.get<{ status: string; loginResponse: any }>(
        `/auth/wechat/ticket/${ticketId.value}/status`)
      if (result.status === 'CONFIRMED' && result.loginResponse) {
        stopPolling()
        emit('loginFromResponse', result.loginResponse)
      }
    } catch {
      stopPolling()
      ticketExpired.value = true
    }
  }, 2000)
}

function stopPolling() {
  if (pollTimer.value) {
    clearInterval(pollTimer.value)
    pollTimer.value = null
  }
}

function switchToPassword() {
  loginMode.value = 'password'
  stopPolling()
  ticketId.value = null
  qrImageBase64.value = null
  ticketExpired.value = false
  nextTick(() => {
    if (form.value.username) {
      passwordInputRef.value?.focus()
    } else {
      usernameInputRef.value?.focus()
    }
  })
}

function handleEnter(event: KeyboardEvent) {
  if (event.isComposing) return
  const activeEl = document.activeElement
  const usernameInputEl = usernameInputRef.value?.input as HTMLInputElement | undefined
  // 如果当前光标在账号框且密码尚未填写，优先平滑聚焦到密码框
  if (activeEl && usernameInputEl && activeEl === usernameInputEl && !form.value.password) {
    event.preventDefault()
    passwordInputRef.value?.focus()
    return
  }
  // 其余场景直接触发登录确认提交
  event.preventDefault()
  submit()
}

function submit() {
  if (props.loading) {
    return
  }
  const username = form.value.username.trim()
  const password = form.value.password
  if (!username) {
    activeError.value = '请输入账号或工号'
    hasShake.value = true
    setTimeout(() => { hasShake.value = false }, 400)
    usernameInputRef.value?.focus()
    return
  }
  if (!password) {
    activeError.value = '请输入登录密码'
    hasShake.value = true
    setTimeout(() => { hasShake.value = false }, 400)
    passwordInputRef.value?.focus()
    return
  }
  activeError.value = ''
  emit('submit', username, password, tenantDomain.trim())
}
</script>

<template>
  <main class="login-screen">
    <!-- 装饰性环境微光晕 -->
    <div class="ambient-glow glow-top" aria-hidden="true" />
    <div class="ambient-glow glow-bottom" aria-hidden="true" />

    <div class="login-wrapper">
      <RrCard class="login-card" :class="{ 'is-shaking': hasShake }">
        <!-- 头部品牌与主标题（已去除冗余副标题） -->
        <header class="card-header">
          <div class="brand-badge">
            <img
              v-if="loginConfig?.loginPageLogoUrl"
              :src="loginConfig.loginPageLogoUrl"
              alt="Logo"
              class="login-logo"
            />
            <div v-else class="brand-icon-box" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
              </svg>
            </div>
          </div>
          <h1 class="main-title">{{ loginConfig?.loginPageTitle || '登录 SaaS 工作台' }}</h1>
        </header>

        <!-- 平滑过渡的统一公共错误提示组件 RrAlert -->
        <transition name="error-slide">
          <div v-if="activeError" class="error-wrapper">
            <RrAlert
              type="error"
              show-icon
              :message="activeError"
            />
          </div>
        </transition>

        <!-- 密码登录模式（全方位绑定回车确认提交） -->
        <div v-if="loginMode === 'password'" class="form-body" @keydown.enter="handleEnter">
          <RrForm layout="vertical" @submit.prevent="submit" @finish="submit">
            <RrFormItem label="账号">
              <RrInput
                ref="usernameInputRef"
                v-model:value="form.username"
                size="large"
                :disabled="loading"
                :status="activeError ? 'error' : undefined"
                autocomplete="username"
                placeholder="请输入用户名或工号"
                allow-clear
                @press-enter="handleEnter"
              >
                <template #prefix>
                  <User :size="16" class="input-icon" />
                </template>
              </RrInput>
            </RrFormItem>

            <RrFormItem label="密码">
              <RrInput
                ref="passwordInputRef"
                v-model:value="form.password"
                type="password"
                size="large"
                :disabled="loading"
                :status="activeError ? 'error' : undefined"
                autocomplete="current-password"
                placeholder="请输入登录密码"
                @press-enter="submit"
              >
                <template #prefix>
                  <Lock :size="16" class="input-icon" />
                </template>
              </RrInput>
            </RrFormItem>

            <div class="submit-row">
              <RrButton
                type="primary"
                size="large"
                block
                html-type="submit"
                :loading="loading"
              >
                {{ loading ? '正在登录…' : '登 录' }}
              </RrButton>
            </div>
          </RrForm>

          <div v-if="loginConfig?.wechatScanEnabled" class="mode-switch-row">
            <RrButton
              type="text"
              size="small"
              class="switch-mode-btn"
              @click="switchToWechat"
            >
              <template #icon>
                <QrCode :size="14" />
              </template>
              切换为微信扫码登录
            </RrButton>
          </div>
        </div>

        <!-- 微信扫码模式 -->
        <div v-else-if="loginMode === 'wechat'" class="qr-mode-body">
          <div class="qr-frame">
            <div v-if="!qrImageBase64 && !ticketExpired" class="qr-loading-box">
              <RefreshCw class="spin-icon" :size="20" />
              <span>正在生成二维码…</span>
            </div>
            <template v-else>
              <img
                v-if="qrImageBase64"
                :src="'data:image/png;base64,' + qrImageBase64"
                alt="微信扫码登录二维码"
                class="qr-display-img"
              />
              <div v-if="ticketExpired" class="qr-expired-overlay">
                <p>二维码已失效</p>
                <RrButton size="small" @click="switchToWechat">
                  <template #icon>
                    <RefreshCw :size="14" />
                  </template>
                  刷新二维码
                </RrButton>
              </div>
            </template>
          </div>
          <p class="qr-instruction">请使用微信扫一扫，在小程序中确认登录</p>
          <div v-if="loginConfig?.passwordLoginEnabled" class="mode-switch-row">
            <RrButton
              type="text"
              size="small"
              class="switch-mode-btn"
              @click="switchToPassword"
            >
              ← 返回账号密码登录
            </RrButton>
          </div>
        </div>
      </RrCard>

      <!-- 底部版权声明 -->
      <footer class="login-footer">
        徐州广播电视传媒集团 · 新聚浪平台 &copy; 2026
      </footer>
    </div>
  </main>
</template>

<style scoped>
.login-screen {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--rr-color-canvas);
  background-image:
    radial-gradient(circle at 12% 15%, rgba(90, 70, 224, 0.16) 0%, transparent 42%),
    radial-gradient(circle at 88% 85%, rgba(90, 70, 224, 0.14) 0%, transparent 48%),
    radial-gradient(circle at 50% 50%, rgba(90, 70, 224, 0.04) 0%, transparent 65%);
  overflow: hidden;
  padding: 24px;
  box-sizing: border-box;
}

/* 现代科技点阵背景 */
.login-screen::before {
  content: "";
  position: absolute;
  inset: 0;
  background-image: radial-gradient(rgba(23, 26, 31, 0.09) 1.2px, transparent 1.2px);
  background-size: 24px 24px;
  mask-image: radial-gradient(ellipse at center, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0.3) 75%, transparent 100%);
  -webkit-mask-image: radial-gradient(ellipse at center, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0.3) 75%, transparent 100%);
  pointer-events: none;
  z-index: 0;
}

/* 背景流动光斑 */
.ambient-glow {
  position: absolute;
  width: 600px;
  height: 600px;
  border-radius: 50%;
  pointer-events: none;
  filter: blur(140px);
  opacity: 0.42;
  z-index: 0;
}

.glow-top {
  top: -200px;
  left: -150px;
  background: radial-gradient(circle, var(--rr-color-primary) 0%, rgba(90, 70, 224, 0) 70%);
}

.glow-bottom {
  bottom: -200px;
  right: -150px;
  background: radial-gradient(circle, var(--rr-color-primary-border) 0%, rgba(198, 185, 243, 0) 70%);
}

.login-wrapper {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 400px;
  gap: 20px;
}

/* 毛玻璃半透明悬浮卡片 */
.login-card {
  width: 100%;
  box-sizing: border-box;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.88);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border: 1px solid rgba(255, 255, 255, 0.95);
  box-shadow:
    0 24px 48px -12px rgba(90, 70, 224, 0.14),
    0 8px 24px -4px rgba(23, 26, 31, 0.04),
    inset 0 1px 1px rgba(255, 255, 255, 0.9);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.login-card :deep(.rr-card__body) {
  padding: 36px 32px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* 错误震动动画 */
.login-card.is-shaking {
  animation: card-shake 0.3s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
}

@keyframes card-shake {
  10%, 90% { transform: translate3d(-1px, 0, 0); }
  20%, 80% { transform: translate3d(2px, 0, 0); }
  30%, 50%, 70% { transform: translate3d(-4px, 0, 0); }
  40%, 60% { transform: translate3d(4px, 0, 0); }
}

/* 头部样式 */
.card-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;
}

.brand-badge {
  display: flex;
  align-items: center;
  justify-content: center;
}

.brand-icon-box {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: var(--rr-color-primary-soft);
  color: var(--rr-color-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(90, 70, 224, 0.12);
}

.login-logo {
  max-height: 48px;
  width: auto;
  object-fit: contain;
}

.main-title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: var(--rr-color-text-strong);
  line-height: 1.3;
  letter-spacing: -0.2px;
}

.error-wrapper {
  margin-bottom: 2px;
}

/* 错误提示平滑展开与收起动效 */
.error-slide-enter-active,
.error-slide-leave-active {
  transition: all 0.24s cubic-bezier(0.4, 0, 0.2, 1);
  max-height: 80px;
  overflow: hidden;
}

.error-slide-enter-from,
.error-slide-leave-to {
  opacity: 0;
  max-height: 0;
  transform: translateY(-6px);
  margin-bottom: 0;
}

/* 表单字段样式 */
.form-body {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.form-body :deep(.rr-form-item) {
  margin-bottom: 16px;
}

.form-body :deep(.rr-form-item__label label) {
  font-size: 13px;
  font-weight: 600;
  color: var(--rr-color-text-strong);
}

.input-icon {
  color: var(--rr-color-text-subtle);
}

.submit-row {
  margin-top: 8px;
}

/* 模式切换 */
.mode-switch-row {
  display: flex;
  justify-content: center;
  margin-top: 6px;
}

.switch-mode-btn {
  color: var(--rr-color-primary);
  font-size: 13px;
  font-weight: 500;
}

.switch-mode-btn:hover {
  color: var(--rr-color-primary-hover);
}

/* 微信扫码模式 */
.qr-mode-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
}

.qr-frame {
  position: relative;
  width: 200px;
  height: 200px;
  border: 1px solid var(--rr-color-border);
  border-radius: 8px;
  background: #ffffff;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.qr-display-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.qr-loading-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  color: var(--rr-color-text-muted);
  font-size: 13px;
}

.spin-icon {
  animation: spin-anim 1s linear infinite;
  color: var(--rr-color-primary);
}

@keyframes spin-anim {
  to { transform: rotate(360deg); }
}

.qr-expired-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.92);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.qr-expired-overlay p {
  margin: 0;
  color: var(--rr-color-danger);
  font-size: 13px;
  font-weight: 600;
}

.qr-instruction {
  margin: 0;
  font-size: 13px;
  color: var(--rr-color-text-muted);
  text-align: center;
}

/* 页脚 */
.login-footer {
  font-size: 12px;
  color: var(--rr-color-text-muted);
  text-align: center;
}
</style>
